// The one CSV codec for the Plan A pipeline. Previously this exact
// quoted-field state machine was hand-reimplemented in three places
// (lib/planASheet.ts, scripts/readPlanASheet.ts,
// scripts/planA/applySheetUpdates.ts); they all now import from here.

type CsvParseState = { rows: string[][]; row: string[]; field: string; inQuotes: boolean }

// A finished field ends the current cell; a finished row also starts a new one.
function pushField(state: CsvParseState): void {
  state.row.push(state.field)
  state.field = ''
}
function pushRow(state: CsvParseState): void {
  pushField(state)
  state.rows.push(state.row)
  state.row = []
}

// Inside quotes everything is literal except `""` (an escaped quote) and a
// lone `"` (which closes the quoted run). Returns the index to resume at.
function consumeQuotedChar(content: string, i: number, state: CsvParseState): number {
  if (content[i] !== '"') { state.field += content[i]; return i + 1 }
  if (content[i + 1] === '"') { state.field += '"'; return i + 2 }
  state.inQuotes = false
  return i + 1
}

// Outside quotes: `"` opens a quoted run, `,` ends a field, `\n` ends a row,
// `\r` is dropped (CRLF tolerance), anything else is literal text.
function consumeUnquotedChar(content: string, i: number, state: CsvParseState): number {
  const c = content[i]
  if (c === '"') state.inQuotes = true
  else if (c === ',') pushField(state)
  else if (c === '\n') pushRow(state)
  else if (c !== '\r') state.field += c
  return i + 1
}

// Minimal RFC 4180-ish parser: quoted fields, escaped quotes, and quoted
// multi-line cells. (Multi-line cells are why we can't just split on '\n'.)
export function parseCsv(content: string): string[][] {
  const state: CsvParseState = { rows: [], row: [], field: '', inQuotes: false }
  let i = 0
  while (i < content.length) {
    i = state.inQuotes ? consumeQuotedChar(content, i, state) : consumeUnquotedChar(content, i, state)
  }
  if (state.field !== '' || state.row.length > 0) pushRow(state) // flush trailing cell/row
  return state.rows
}

export type CsvLineEndingConventions = { lineEnding: '\n' | '\r\n'; trailingNewline: boolean }

// A field needs quoting iff it contains the delimiter, a quote, or a newline.
function escapeCsvField(field: string): string {
  return /[",\n\r]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field
}

export function serializeCsv(
  rows: string[][],
  options: CsvLineEndingConventions = { lineEnding: '\n', trailingNewline: true },
): string {
  const body = rows.map(row => row.map(escapeCsvField).join(',')).join(options.lineEnding)
  return options.trailingNewline ? body + options.lineEnding : body
}

// Sniff the original file's line ending + trailing-newline convention so we
// round-trip a CSV without spuriously rewriting every line.
export function detectCsvLineEndingConventions(text: string): CsvLineEndingConventions {
  return { lineEnding: /\r\n/.test(text) ? '\r\n' : '\n', trailingNewline: /\r?\n$/.test(text) }
}
